import React, { useCallback, useEffect, useMemo } from "react";
import { MenuItem, Select, TextField, Typography } from "@mui/material";
import {
  AxisOptions,
  FormattingRule,
  FormattingRuleType,
  createFormattingRule,
  deleteFormattingInstructionValues,
  deleteFormattingInstructions,
  getFormattingRule,
  getFormattingRules,
  updateFormattingInstruction,
} from "@coolset/cs-ui";
import { isColumn, letterToNumber, numberToLetter } from "@coolset/cs-ui";

const AxisTypes = [
  {
    title: "X axis",
    queryRules: { axis: AxisOptions.X },
  },
  {
    title: "Y axis",
    queryRules: { axis: AxisOptions.Y },
  },
  {
    title: "Label",
    queryRules: { isLabel: true },
  },
];

const FirstLettersRegex = /^[A-Z]*/g;

export const AxisInputs = (props: AxisInputsProps) => {
  const { formattingInstructions, maxColumns, onChange, disableYAxis, disableAxisRange } = props;

  const axisRules = useMemo(
    () => getFormattingRules(formattingInstructions, { ruleType: FormattingRuleType.Axis }),
    [formattingInstructions]
  );

  const activeAxisTypes = useMemo(() => {
    if (disableYAxis) {
      return AxisTypes.filter((axisType) => axisType.queryRules.axis !== "y");
    }

    return AxisTypes;
  }, [disableYAxis]);

  const handleAxisChange = useCallback(
    (colLetter: string, queryRules: Partial<FormattingRule>) => {
      const rule = getFormattingRule(axisRules, queryRules);
      if (!rule) {
        onChange(
          createFormattingRule(formattingInstructions, {
            ruleType: FormattingRuleType.Axis,
            position: colLetter,
            name: colLetter,
            ...queryRules
          }, true)[0]
        );

        return;
      }

      if (!colLetter) {
        onChange(deleteFormattingInstructionValues(formattingInstructions, rule, ["axis", 'name', 'isLabel'])[0]);
      } else {
        onChange(
          updateFormattingInstruction(
            formattingInstructions,
            {
              ...rule,
              position: colLetter,
              name: colLetter,           
            },
            true
          )[0]
        );
      }
    },
    [axisRules, formattingInstructions, onChange]
  );

  const handleRangeChange = useCallback(
    (value: string, rangeLabel: string, queryRules: Partial<FormattingRule>) => {
      const rule = getFormattingRule(axisRules, queryRules);
      if (!rule) return;

      onChange(
        updateFormattingInstruction(formattingInstructions, {
          ...rule,
          [rangeLabel]: value,
        })[0]
      );
    },
    [axisRules, formattingInstructions, onChange]
  );

  useEffect(() => {
    activeAxisTypes.forEach((axisType) => {
      const rule = getFormattingRule(axisRules, axisType.queryRules);
      if (!rule || !isColumn(rule?.position)) return;

      if (letterToNumber(rule.position) > maxColumns) {
        onChange(deleteFormattingInstructions(formattingInstructions, rule.ruleId)[0]);
      }
    });
  }, [activeAxisTypes, axisRules, formattingInstructions, maxColumns, onChange]);

  return (
    <>
      <div className={`col-container:${disableYAxis ? 2 : 3}`}>
        {activeAxisTypes.map((axisType) => (
          <div key={axisType.title} className="_d-grid-sm-gap">
            <Typography variant="h4" color="text.secondary">
              {axisType.title}
            </Typography>
            <Select
              value={
                getFormattingRule(axisRules, {
                  ruleType: FormattingRuleType.Axis,
                  ...axisType.queryRules,
                })?.position.match(FirstLettersRegex)?.[0] ?? ""
              }
              onChange={(e) => handleAxisChange(e.target.value, axisType.queryRules)}
            >
              {[null, ...Array.from({ length: maxColumns }, (_, i) => i + 1)].map((colNum, i) => (
                <MenuItem key={i} value={colNum ? numberToLetter(colNum) : ""}>
                  {colNum ? numberToLetter(colNum) : "None"}
                </MenuItem>
              ))}
            </Select>
          </div>
        ))}
      </div>

      {!disableAxisRange && (
        <div className="_d-flex-dir-col-jc-sb-sm-gap">
          {axisRules
            .filter((rule) => !rule.isLabel)
            ?.map((rule) => {
              const axis = activeAxisTypes.find((axisType) => axisType.queryRules.axis === rule.axis);
              return axis ? (
                <div
                  className="_d-flex-dir-col-sm-gap"
                  key={rule.position}
                  style={{ order: rule.axis!.toLowerCase().charCodeAt(0) - "a".charCodeAt(0) }}
                >
                  <Typography variant="h4" color="text.secondary">
                    {`${axis?.title} Range`}
                  </Typography>
                  <div className="_d-flex-sm-gap">
                    {["Min", "Max"].map((label) => (
                      <div className="_d-flex-ali-center-sm-gap" key={label}>
                        <Typography variant="h5" color="text.secondary">
                          {label}
                        </Typography>
                        <TextField
                          onChange={(e) => handleRangeChange(e.target.value, `range${label}`, { axis: rule.axis })}
                          type="number"
                          value={rule[`range${label}` as "rangeMin" | "rangeMax"] ?? ""}
                        />
                      </div>
                    ))}
                  </div>
                  {!!rule.rangeMin && !!rule.rangeMax && rule.rangeMin > rule.rangeMax && (
                    <Typography color="error" px={4} variant="subtitle1">
                      Error: Min range is higher than Max range
                    </Typography>
                  )}
                </div>
              ) : null;
            })}
        </div>
      )}
    </>
  );
};

interface AxisInputsProps {
  formattingInstructions: FormattingRule[];
  maxColumns: number;
  onChange: (rules: FormattingRule[]) => void;
  disableAxisRange?: boolean;
  disableYAxis?: boolean;
}
